import { useMemo } from 'react'
import styled from 'styled-components'
import { theme } from '../../../theme/theme'
import { BodyMedium, Title4 } from '../../../components/styles/Typography'
import { useGetSymptomGraphsQuery } from '../../../api/ester-b2b-api/private/symptomProgress/symptomProgressQueries'
import { SymptomCategory } from '@estercare/ester-shared'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { isMobile } from '../../../utils/devices'
import { Stack } from '@mui/material'
import { SingleLineChart } from '../../../components/charts/SingleLineChart'
import { MultiLineChart } from '../../../components/charts/MultiLineChart'
import { useTranslation } from 'react-i18next'
import { getDateFnsLocale } from '../../../utils/dates'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import {
  buildBreakdownData,
  buildBreakdownLines,
  buildGeneralWellbeingData,
  buildScale,
  buildTopSymptomsData,
  buildSymptomLines,
} from '../../../utils/statistics/symptomGraphs'

const SCALE_0_10 = [0, 2, 4, 6, 8, 10]

export const HealthProgress = () => {
  const { t, i18n } = useTranslation()
  const width = useWindowWidth()
  const isMobileDevice = isMobile(width)
  const locale = getDateFnsLocale(i18n.language)

  const { data: symptomGraphsData } = useGetSymptomGraphsQuery()
  const symptoms = symptomGraphsData?.symptoms

  const wellbeingData = useMemo(
    () =>
      buildGeneralWellbeingData(
        symptoms?.find((symptom) => symptom.category === SymptomCategory.GeneralWellbeing),
        locale
      ),
    [symptoms, locale]
  )

  const { topSymptomsData, topSymptomsLines } = useMemo(() => {
    const topSymptoms = symptoms?.filter((symptom) => symptom.isTopSymptom) ?? []
    return {
      topSymptomsData: buildTopSymptomsData(topSymptoms, locale),
      topSymptomsLines: buildSymptomLines(topSymptoms),
    }
  }, [symptoms, locale])

  const breakdownSymptoms = useMemo(
    () => symptoms?.filter((symptom) => symptom.category !== SymptomCategory.GeneralWellbeing) ?? [],
    [symptoms]
  )

  return (
    <ComponentLayout>
      <InnerContainer>
        <ChartContainer gap={2}>
          <Title4>{t('employee.yourHealth.wellbeing')}</Title4>

          <SingleLineChart
            isMobileDevice={isMobileDevice}
            data={wellbeingData}
            title={t('employee.yourHealth.overallWellbeingScore')}
            color={theme.color.brightBlue}
            withGradient
          />
        </ChartContainer>

        <ChartContainer gap={2}>
          <Title4>{t('employee.yourHealth.symptomTrendsTitle')}</Title4>
          <BodyMedium>{t('employee.yourHealth.symptomTrendsDescription')}</BodyMedium>

          <MultiLineChart
            isMobileDevice={isMobileDevice}
            data={topSymptomsData}
            lines={topSymptomsLines}
            title={t('employee.yourHealth.topSymptomsOverview')}
            label=""
            scale={SCALE_0_10}
            legendVariant="pill"
          />
        </ChartContainer>

        <ChartContainer gap={2}>
          <Title4>{t('employee.yourHealth.graphsBreakdownTitle')}</Title4>
          <BodyMedium>{t('employee.yourHealth.graphsBreakdownDescription')}</BodyMedium>

          <BreakdownList>
            {breakdownSymptoms.map((symptom) => (
              <MultiLineChart
                key={symptom.category}
                isMobileDevice={isMobileDevice}
                data={buildBreakdownData(symptom, locale)}
                lines={buildBreakdownLines(symptom)}
                title={symptom.label}
                label=""
                scale={buildScale(symptom.maxValue)}
                chartType={symptom.chartType}
                collapsible={isMobileDevice}
                defaultOpen={false}
              />
            ))}
          </BreakdownList>
        </ChartContainer>
      </InnerContainer>
    </ComponentLayout>
  )
}

const BreakdownList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.large}px;

  @media screen and (max-width: ${theme.breakpoint.small}px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.small}px;
  }
`

const ChartContainer = styled(Stack)`
  max-width: 1160px;
  margin: 0 ${theme.spacing.large}px ${theme.spacing.jumbo}px ${theme.spacing.large}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    margin: 0 ${theme.spacing.small}px ${theme.spacing.xxlarge}px ${theme.spacing.small}px;
  }
`

const InnerContainer = styled.div<{ $backgroundColor?: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'transparent'};
`
